/**
 * Diplomexa Verify — public web QR scanner.
 *
 * Mobile-first, no-auth-required scanner that uses the device camera to read a
 * Diplomexa certificate QR code, decode the verify URL (`/verify/CODE?sig=…`),
 * and call the existing /api/certificates/verify/{code}?sig=… endpoint.
 *
 * Renders three states:
 *   1. Scan — live camera stream + reticle
 *   2. Result — animated success or fail card
 *   3. Manual — fallback typed code entry
 *
 * Installable as PWA via /public/manifest.json + /public/diplomexa-icon.png.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import api from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ShieldCheck,
  ShieldX,
  Camera,
  RefreshCcw,
  Keyboard,
  Loader2,
  ArrowLeft,
  QrCode,
} from "lucide-react";

const SCAN_ELEMENT_ID = "diplomexa-verify-reader";

/** Extracts `{code, sig}` from any URL or returns null. */
function parseVerifyPayload(input) {
  if (!input) return null;
  let url;
  try {
    url = new URL(input.trim());
  } catch {
    // Not a URL — assume the user pasted the raw code
    const code = input.trim().toUpperCase();
    return code ? { code, sig: "" } : null;
  }
  // Expect /verify/CODE
  const match = url.pathname.match(/\/verify\/([^/?#]+)/);
  if (!match) return null;
  const code = decodeURIComponent(match[1]).toUpperCase();
  const sig = url.searchParams.get("sig") || "";
  return { code, sig };
}

export default function VerifyScanPage() {
  const [view, setView] = useState("scan"); // 'scan' | 'result' | 'manual'
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const scannerRef = useRef(null);
  const isStarted = useRef(false);

  // ---------- Scanner lifecycle ----------
  useEffect(() => {
    if (view !== "scan") return undefined;
    let cancelled = false;
    const start = async () => {
      try {
        if (!scannerRef.current) {
          scannerRef.current = new Html5Qrcode(SCAN_ELEMENT_ID, {
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          });
        }
        await scannerRef.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            if (cancelled || isStarted.current === false) return;
            isStarted.current = false;
            try {
              await scannerRef.current.stop();
            } catch { /* scanner already stopped — proceed to verify */ }
            await verify(decoded);
          },
          () => {} // soft errors silently ignored
        );
        isStarted.current = true;
        setScanError(null);
      } catch (e) {
        setScanError(
          e?.message?.includes("Permission")
            ? "Camera permission denied. Allow camera access in your browser settings, or use Type code."
            : "Could not start the camera on this device. Try Type code below."
        );
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s && isStarted.current) {
        isStarted.current = false;
        s.stop().catch(() => {});
      }
    };
  }, [view]);

  // ---------- Verify flow ----------
  const verify = async (rawInput) => {
    setBusy(true);
    const parsed = parseVerifyPayload(rawInput);
    if (!parsed?.code) {
      setResult({ ok: false, reason: "unreadable", raw: rawInput });
      setView("result");
      setBusy(false);
      return;
    }
    try {
      const qs = parsed.sig ? `?sig=${encodeURIComponent(parsed.sig)}` : "";
      const { data } = await api.get(`/certificates/verify/${parsed.code}${qs}`);
      setResult({
        ok: data.valid,
        signedQr: !!data.signature_valid,
        certificate: data.certificate,
        code: parsed.code,
      });
    } catch {
      setResult({ ok: false, reason: "network", code: parsed.code });
    }
    setBusy(false);
    setView("result");
  };

  const reset = () => {
    setResult(null);
    setManualCode("");
    setView("scan");
  };

  // ---------- Render ----------
  return (
    <div
      className="min-h-screen text-white relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 20% 0%, #14264F 0%, #0A192F 55%, #050C1B 100%)",
      }}
      data-testid="verify-scan-page"
    >
      {/* subtle gold grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(212,175,55,0.6) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />

      <header className="relative px-5 pt-5 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="verify-back-home">
          <BrandLogo light />
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40">
          <QrCode className="h-3 w-3" /> Verify
        </span>
      </header>

      <main className="relative max-w-md mx-auto px-5 pt-10 pb-20">
        <AnimatePresence mode="wait">
          {view === "scan" && (
            <motion.section
              key="scan"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold">
                Diplomexa Verify
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tighter">
                Scan a certificate
              </h1>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                Point your camera at the QR code printed on a Diplomexa certificate to instantly verify its authenticity.
              </p>

              {/* viewport */}
              <div className="mt-8 relative rounded-[28px] overflow-hidden bg-black aspect-square ring-1 ring-white/10">
                <div id={SCAN_ELEMENT_ID} className="w-full h-full" />
                {/* Reticle overlay */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="relative w-[60%] h-[60%]">
                    <Corner pos="top-0 left-0" />
                    <Corner pos="top-0 right-0 rotate-90" />
                    <Corner pos="bottom-0 left-0 -rotate-90" />
                    <Corner pos="bottom-0 right-0 rotate-180" />
                    {/* Sweeping line */}
                    <motion.div
                      initial={{ y: 0, opacity: 0.6 }}
                      animate={{ y: ["0%", "100%", "0%"], opacity: [0.6, 1, 0.6] }}
                      transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute left-2 right-2 h-[2px] bg-[#D4AF37] shadow-[0_0_18px_rgba(212,175,55,0.7)]"
                    />
                  </div>
                </div>
                {busy && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                  </div>
                )}
              </div>

              {scanError && (
                <p
                  className="mt-4 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3"
                  data-testid="verify-scan-error"
                >
                  {scanError}
                </p>
              )}

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Button
                  onClick={() => setView("manual")}
                  variant="outline"
                  className="border-white/15 bg-white/5 text-white hover:bg-white/10"
                  data-testid="verify-type-code-button"
                >
                  <Keyboard className="h-3.5 w-3.5 mr-1.5" /> Type code
                </Button>
                <Button
                  onClick={() => {
                    if (scannerRef.current && isStarted.current) {
                      scannerRef.current.stop().catch(() => {});
                    }
                    isStarted.current = false;
                    setView("scan");
                  }}
                  variant="outline"
                  className="border-white/15 bg-white/5 text-white hover:bg-white/10"
                  data-testid="verify-restart-button"
                >
                  <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Restart camera
                </Button>
              </div>
            </motion.section>
          )}

          {view === "manual" && (
            <motion.section
              key="manual"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <button
                onClick={reset}
                className="text-xs text-slate-400 hover:text-white inline-flex items-center"
                data-testid="verify-manual-back"
              >
                <ArrowLeft className="h-3 w-3 mr-1" /> Back to scanner
              </button>
              <h1 className="mt-4 text-3xl font-bold tracking-tighter">
                Enter the certificate code
              </h1>
              <p className="mt-2 text-sm text-slate-400">
                Look for the alphanumeric code printed under the QR on the certificate.
              </p>

              <div className="mt-8 rounded-2xl bg-white/5 border border-white/10 p-5">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  placeholder="GLOBEX-2026-BD-001"
                  className="bg-transparent border-white/15 text-white placeholder-slate-500 font-mono uppercase tracking-wide"
                  data-testid="verify-manual-input"
                />
                <Button
                  onClick={() => verify(manualCode)}
                  disabled={!manualCode || busy}
                  className="mt-4 w-full bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
                  data-testid="verify-manual-submit"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Verify
                </Button>
              </div>
            </motion.section>
          )}

          {view === "result" && result && (
            <motion.section
              key="result"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              data-testid="verify-result"
            >
              {result.ok ? (
                <SuccessCard result={result} onScanAnother={reset} />
              ) : (
                <FailureCard result={result} onTryAgain={reset} />
              )}
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      <footer className="absolute bottom-0 inset-x-0 px-6 py-4 text-center text-[10px] uppercase tracking-[0.22em] text-slate-500">
        Diplomexa · From Conferences to Global Citizens
      </footer>
    </div>
  );
}

// ----------------------------------------------------------------------------
function Corner({ pos }) {
  return (
    <div
      className={`absolute w-7 h-7 border-t-2 border-l-2 border-[#D4AF37] ${pos}`}
      style={{ borderRadius: 6 }}
    />
  );
}

function SuccessCard({ result, onScanAnother }) {
  const c = result.certificate || {};
  return (
    <div data-testid="verify-success">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 16 }}
        className="mx-auto h-24 w-24 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center"
      >
        <ShieldCheck className="h-12 w-12 text-emerald-400" />
      </motion.div>
      <p className="mt-6 text-center text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-bold">
        Authentic
      </p>
      <h2 className="mt-2 text-center text-3xl font-serif text-white">{c.delegate_name}</h2>
      <p className="mt-2 text-center text-sm text-slate-400">{c.event_title}</p>

      {result.signedQr && (
        <div className="mt-5 flex justify-center" data-testid="verify-qr-stamp">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full bg-[#0A192F] text-[#D4AF37] border border-[#D4AF37]/40">
            <QrCode className="h-3 w-3" /> Verified via QR
          </span>
        </div>
      )}

      <div className="mt-8 rounded-2xl bg-white/5 border border-white/10 p-5 text-sm divide-y divide-white/10">
        <Row label="Award" value={c.award_type} />
        <Row label="Council" value={c.council} />
        <Row label="Country" value={c.country} />
        <Row label="Organiser" value={c.organization_name} />
        <Row
          label="Issued"
          value={c.issued_at ? new Date(c.issued_at).toLocaleDateString() : "—"}
        />
        <Row label="Code" value={c.code} mono />
      </div>

      <Button
        onClick={onScanAnother}
        className="mt-6 w-full bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
        data-testid="verify-scan-another"
      >
        <Camera className="h-4 w-4 mr-2" /> Scan another
      </Button>
    </div>
  );
}

function FailureCard({ result, onTryAgain }) {
  const message =
    result.reason === "network"
      ? "We couldn't reach the verification service. Check your connection and try again."
      : result.reason === "unreadable"
        ? "That QR code didn't match a Diplomexa certificate. Make sure you're scanning the QR printed on the certificate."
        : "This certificate could not be verified. It may be forged, revoked, or issued by a different platform.";
  return (
    <div data-testid="verify-failure">
      <motion.div
        initial={{ rotate: 0, scale: 0.6, opacity: 0 }}
        animate={{ rotate: [0, -8, 8, -6, 0], scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mx-auto h-24 w-24 rounded-full bg-red-500/15 border border-red-400/40 flex items-center justify-center"
      >
        <ShieldX className="h-12 w-12 text-red-400" />
      </motion.div>
      <p className="mt-6 text-center text-[10px] uppercase tracking-[0.28em] text-red-300 font-bold">
        Could not verify
      </p>
      <h2 className="mt-2 text-center text-2xl font-bold text-white">
        {result.code ? `Code ${result.code} not recognised` : "Unrecognised QR code"}
      </h2>
      <p className="mt-3 text-center text-sm text-slate-400 leading-relaxed">{message}</p>
      <Button
        onClick={onTryAgain}
        className="mt-8 w-full bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
        data-testid="verify-try-again"
      >
        <RefreshCcw className="h-4 w-4 mr-2" /> Try again
      </Button>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
        {label}
      </span>
      <span className={`text-white text-sm ${mono ? "font-mono" : "font-semibold"}`}>
        {value || "—"}
      </span>
    </div>
  );
}
